/**
 * Web Health report header: the twin of `HealthReportHeader.tsx`.
 *
 * The rings with a spoken summary, then, in plain words: the heading "Your
 * CV scores 72 out of 100" (or the caller's `heading`), "Up from 61" when
 * `previousScore` differs, the line "3 quick fixes will make it stronger." /
 * "Nothing to fix. Nice work." (or the caller's `detail`), and a quiet "Some
 * checks couldn't finish." for a partial report. Nothing under the rings
 * while checking. An unreadable CV shows no rings, only "We couldn't read
 * enough of this CV." and the way forward.
 *
 * The rings stay mounted while `report` changes, so a new score animates the
 * arcs from the old fractions to the new ones.
 */
import { useTranslation } from 'react-i18next';
import { getCenterValue, getRingValues } from '@/lib/cv-health-model';
import { HealthRings } from './HealthRings';
import {
  getHeadingDetailKey,
  getRingsAccessibilityLabel,
  getScoreDeltaRef,
  getSimpleDetailRef,
  getSimpleHeadingRef,
  translateRef,
} from './cv-health-copy';
import { HEALTH_RING_REFERENCE_SIZE } from './health-rings-geometry';
import type { HealthReportHeaderProps } from './health-props';

export type { HealthReportHeaderProps };

export function HealthReportHeader({
  report,
  mode,
  size = HEALTH_RING_REFERENCE_SIZE,
  previousScore,
  heading,
  detail,
  variant = 'onboarding',
}: HealthReportHeaderProps) {
  const { t } = useTranslation('common');

  if (report?.status === 'unreadable') {
    return (
      <div className="tabbio-health tabbio-health-header">
        <h2 className="tabbio-health-heading">{t('cvHealth.heading.unreadable')}</h2>
        <p className="tabbio-health-heading-detail is-body">{t('cvHealth.heading.unreadableBody')}</p>
      </div>
    );
  }

  const checking = mode === 'checking';
  const derived = checking ? null : getSimpleHeadingRef(report, variant);
  const headingText =
    heading !== undefined ? heading : derived ? translateRef(t, derived) : null;
  const derivedDetail = checking ? null : getSimpleDetailRef(report, variant);
  const detailText =
    detail !== undefined ? detail : derivedDetail ? translateRef(t, derivedDetail) : null;
  const delta = checking ? null : getScoreDeltaRef(report?.score, previousScore);
  const partialKey = checking ? null : getHeadingDetailKey(report);

  return (
    <div className="tabbio-health tabbio-health-header">
      <HealthRings
        size={size}
        mode={mode}
        rings={getRingValues(report, mode)}
        center={getCenterValue(report, mode)}
        accessibilityLabel={getRingsAccessibilityLabel(t, report, mode)}
      />
      {headingText ? (
        <h2 className="tabbio-health-heading is-after-rings" aria-live="polite">
          {headingText}
        </h2>
      ) : null}
      {delta ? (
        <p className="tabbio-health-heading-detail">{t(delta.key, { score: delta.score })}</p>
      ) : null}
      {detailText ? <p className="tabbio-health-heading-detail is-body">{detailText}</p> : null}
      {partialKey ? <p className="tabbio-health-heading-detail">{t(partialKey)}</p> : null}
    </div>
  );
}

export default HealthReportHeader;
